import { Trans, useTranslation } from 'react-i18next';

import useResetSystemTemplateMutation from 'components/domain/automation/systemTemplates/useResetSystemTemplateMutation';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { getLanguageName } from 'i18n/i18n.utils';
import { LanguageCode } from 'i18n/internationalization.types';
import { BaseErrorResponse } from 'models/_base';
import { SystemTemplates } from 'models/automation/SystemTemplates';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

const useSystemTemplateResetModal = () => {
  const { t, i18n } = useTranslation();
  const { mutateAsync: resetTemplate } = useResetSystemTemplateMutation();
  const { openConfirmModal } = useAppModal();
  const { notifyError } = useNotify();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.systemTemplates.apiErrorCodes.',
        t,
      }),
    );
  };

  const openSystemTemplateResetModal = ({
    key,
    languageCode,
    onAfterConfirm,
  }: {
    key: SystemTemplates;
    languageCode: LanguageCode;
    onAfterConfirm: () => void;
  }) => {
    openConfirmModal({
      body: (
        <Trans
          i18nKey="pageAutomations.systemTemplates.modalReset.body"
          values={{
            name: t('pageAutomations.systemTemplates.systemTemplateName', {
              context: key,
            }),
            language: getLanguageName(i18n.language, languageCode),
          }}
          components={{
            br: <br />,
          }}
        />
      ),
      confirmColor: 'danger',
      confirmLabel: t('pageAutomations.systemTemplates.modalReset.title'),
      title: t('pageAutomations.systemTemplates.modalReset.title'),
      onConfirmAsync: async () => {
        await resetTemplate({ key, language: languageCode }, { onError });
        onAfterConfirm();
      },
    });
  };

  return { openSystemTemplateResetModal };
};

export default useSystemTemplateResetModal;
